import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BASE_SITE_URL } from "../../config";

const CapturePopup = (props) => {
  const { t } = useTranslation();
  const { latestScreenshot, latestCaptureId } = props.state;
  const [isPublic, setIsPublic] = useState(true); // auto-published by default
  const [toggling, setToggling] = useState(false);
  const [allowTry, setAllowTry] = useState(false);
  const [togglingTry, setTogglingTry] = useState(false);

  const handleTogglePublish = async () => {
    if (!latestCaptureId) return;
    setToggling(true);
    try {
      if (isPublic) {
        await props.postCommands.unpublishFromGallery(latestCaptureId);
        setIsPublic(false);
        toast.success(t("community.unpublish"));
      } else {
        const thumbnail = await props.basicFunctions.generateThumbnail(latestScreenshot);
        await props.postCommands.publishToGallery(latestCaptureId, thumbnail);
        setIsPublic(true);
        toast.success(t("toast.published"));
      }
      props.mainFunctions.refetchProfile();
    } catch (err) {
      console.error("Error toggling publish:", err);
      toast.error(t("toast.publishFailed"));
    } finally {
      setToggling(false);
    }
  };

  const handleToggleTry = async () => {
    if (!latestCaptureId) return;
    setTogglingTry(true);
    try {
      const result = await props.postCommands.toggleAllowTry(latestCaptureId);
      if (result) {
        setAllowTry(result.allow_try === 1);
      }
    } catch (err) {
      console.error("Error toggling allow try:", err);
    } finally {
      setTogglingTry(false);
    }
  };

  const handleDownload = () => {
    if (!latestScreenshot) return;
    const link = document.createElement("a");
    link.href = latestScreenshot;
    link.download = `tier-list-${Date.now()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t("toast.downloaded"));
  };

  const handleShareLink = () => {
    if (!latestCaptureId) return;
    const url = `${BASE_SITE_URL}/share/${latestCaptureId}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success(t("toast.linkCopied") || "Link copied to clipboard!");
    }).catch(() => {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(t("toast.linkCopied") || "Link copied to clipboard!");
    });
  };

  return (
    <Dialog
      id="capture-popup"
      open={true}
      onClose={() => props.basicFunctions.updateState({ popup: "" })}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-soft)",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CameraAltIcon sx={{ color: "var(--accent)", fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("capture.title")}
          </Typography>
          {isPublic && (
            <Chip
              icon={<PublicIcon sx={{ fontSize: 14 }} />}
              label={t("community.published")}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.7rem",
                backgroundColor: "var(--accent-soft)",
                color: "var(--accent)",
                "& .MuiChip-icon": { color: "var(--accent)" },
              }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => props.basicFunctions.updateState({ popup: "" })}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Screenshot preview */}
      <DialogContent sx={{ display: "flex", justifyContent: "center", px: 3, pb: 2 }}>
        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid var(--border-soft)",
            backgroundColor: "var(--surface-3)",
          }}
        >
          <img
            src={latestScreenshot}
            alt={t("capture.fullscreenAlt")}
            style={{
              maxWidth: "100%",
              maxHeight: "65vh",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {latestCaptureId && (
          <>
            <Button
              onClick={handleTogglePublish}
              variant="outlined"
              disabled={toggling}
              startIcon={
                toggling ? (
                  <CircularProgress size={16} />
                ) : isPublic ? (
                  <PublicOffIcon />
                ) : (
                  <PublicIcon />
                )
              }
              sx={{
                textTransform: "none",
                borderColor: isPublic ? "var(--border-mid)" : "var(--accent)",
                color: isPublic ? "var(--border-strong)" : "var(--accent)",
                "&:hover": {
                  borderColor: isPublic ? "var(--border-strong)" : "var(--accent-hover)",
                  backgroundColor: isPublic ? "var(--hover-overlay)" : "var(--accent-soft)",
                },
              }}
            >
              {isPublic ? t("community.unpublish") : t("community.publish")}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={allowTry}
                  onChange={handleToggleTry}
                  disabled={togglingTry}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "var(--accent)" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "var(--accent)" },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PlayArrowIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                  <Typography variant="caption" sx={{ color: "var(--text-secondary)" }}>
                    {t("capture.allowTry")}
                  </Typography>
                </Box>
              }
              sx={{ ml: 0.5 }}
            />
          </>
        )}
        <Button
          onClick={handleDownload}
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{
            textTransform: "none",
            borderColor: "var(--border-mid)",
            color: "var(--text-secondary)",
            "&:hover": { borderColor: "var(--border-strong)", backgroundColor: "var(--hover-overlay)" },
          }}
        >
          {t("capture.download")}
        </Button>
        {latestCaptureId && (
          <Button
            onClick={handleShareLink}
            variant="outlined"
            startIcon={<ShareIcon />}
            sx={{
              textTransform: "none",
              borderColor: "var(--border-mid)",
              color: "var(--text-secondary)",
              "&:hover": { borderColor: "var(--border-strong)", backgroundColor: "var(--hover-overlay)" },
            }}
          >
            {t("capture.share") || "Copy Link"}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          onClick={() => props.basicFunctions.updateState({ popup: "" })}
          sx={{ textTransform: "none", color: "var(--text-secondary)" }}
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CapturePopup;
